<template>
  <div class="pt-8 tile my-4">
    <div class="alert alert-warning" role="alert">
      <h4 class="alert-heading mb-3">{{ trans('dashboard.integrations.flows.filter_options.notice.title') }}</h4>
      <div v-html="trans('dashboard.integrations.flows.filter_options.notice.message')"/>
    </div>
    <div v-if="isUpdate" class="filters">
      <div v-if="!validated" class="alert alert-danger">{{ trans('validation.Please fill out all required fields') }}</div>
      <div v-for="(item, index) in filtersData" :key="`fd-${index}`" class="row align-items-center">
        <div class="form-group col-md-2">
          <label>{{ trans('dashboard.integrations.flows.condition') }}</label>
          <select v-model="item.condition" class="w-full form-control form-select mr-4">
            <option v-for="(option, index) in conditionOptions" :key="index" :value="option.value">
              {{ option.name }}
            </option>
          </select>
        </div>

        <div class="form-group col-md-1">
          <label>{{ trans('dashboard.integrations.flows.start') }}</label>
          <select id="" v-model="item.start" class="w-full form-control form-select mr-4">
            <option v-for="(option, index) in startOptions" :key="`option-start-${index}`" :value="option.value">
              {{ option.name }}
            </option>
          </select>
        </div>

        <div class="form-group col-md-2">
          <label>{{ trans('dashboard.integrations.flows.field_name') }}*</label>
          <input v-model="item.field_name" class="w-full form-control form-input form-input-bordered"/>
        </div>

        <div class="form-group col-md-2">
          <label>{{ trans('dashboard.integrations.flows.operand') }}</label>
          <select id="" v-model="item.operand" class="w-full form-control form-select mr-4">
            <option v-for="(option, index) in operandOptions" :key="`option-${index}`" :value="option.value">
              {{ option.name }}
            </option>
          </select>
        </div>

        <div class="form-group col-md-2">
          <label>{{ trans('dashboard.integrations.flows.value') }}</label>
          <input v-model="item.value" class="w-full form-control form-input form-input-bordered"/>
        </div>

        <div class="form-group col-md-1">
          <label>{{ trans('dashboard.integrations.flows.end') }}</label>
          <select id="" v-model="item.end" class="w-full form-control form-select mr-4">
            <option v-for="(option, index) in endOptions" :key="`option-end-${index}`" :value="option.value">
              {{ option.name }}
            </option>
          </select>
        </div>
        <button
          v-if="filtersData.length > 0 && canUpdate"
          class="ml-4 delete-button cursor-pointer col-md-2"
          type="button"
          @click="removeRow(index)"
        >
          X
        </button>
      </div>

      <div v-if="canUpdate" class="form-group">
        <manual-link-button :manual-link="manualLink"/>
        <button class="button" type="button" @click="addFilterFields">{{ trans('common.add') }}</button>
        <button class="button button_success" type="button" @click="saveFilter">{{ trans('common.save') }}</button>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-11">
        <div v-for="(item, index) in filterOptions" :key="`group-${index}`" class="form-group row">
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.condition') }}</label
          ><input :value="item.condition" class="form-input" disabled
          /></span>
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.start') }}</label
          ><input :value="item.start" class="form-input" disabled
          /></span>
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.field_name') }}</label
          ><input :value="item.field_name" class="form-input" disabled
          /></span>
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.operand') }}</label
          ><input :value="item.operand" class="form-input" disabled
          /></span>
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.value') }}</label
          ><input :value="item.value" class="form-input" disabled
          /></span>
          <span class="col"
          ><label>{{ trans('dashboard.integrations.flows.end') }}</label
          ><input :value="item.end" class="form-input" disabled
          /></span>
        </div>
      </div>

      <div v-if="canUpdate" class="col-1 align-self-center">
        <button class="btn btn-info" @click="handleUpdate()">{{ trans('common.update') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import DashboardLayout from '@/Layouts/DashboardLayout';
import IntegrationFlowLayout from '@/Layouts/IntegrationFlowLayout';
import ManualLinkButton from '@/components/Shared/ManualLinkButton.vue';

export default {
  name: 'FlowFilterOptions',
  components: {ManualLinkButton},

  layout: [DashboardLayout, IntegrationFlowLayout],

  props: {
    user: Object,
    manualLink: String,
  },

  data: () => ({
    conditionOptions: [
      {id: 1, name: 'And', value: '&&'},
      {id: 2, name: 'Or', value: '||'},
    ],
    operandOptions: [
      {name: '===', value: '==='},
      {name: '==', value: '=='},
      {name: '!=', value: '!='},
      {name: '>=', value: '>='},
      {name: '>', value: '>'},
      {name: '<=', value: '<='},
      {name: '<', value: '<'},

      {name: 'Contains', value: 'Contains'},
      {name: 'Not Contains', value: 'Not Contains'},
    ],
    startOptions: [
      {name: '-', value: null},
      {name: '(', value: '('},
      {name: '[[', value: '[['},
    ],
    endOptions: [
      {name: '-', value: null},
      {name: ')', value: ')'},
      {name: ']]', value: ']]'},
    ],
    filtersData: [
      {
        condition: '',
        start: '',
        field_name: '',
        operand: '',
        value: '',
        end: '',
      },
    ],
    validated: true,
    isUpdate: false,
    filterOptionsData: [],
  }),

  computed: {
    filterOptions() {
      return this.filterOptionsData;
    },
    canUpdate() {
      return this.$can('flows.filter-options.update');
    },
  },

  async mounted() {
    await this.fetchOptions();
  },

  methods: {
    addFilterFields() {
      this.filtersData.push({
        condition: '',
        field_name: '',
        operand: '',
        value: '',
      });
    },

    saveFilter() {
      if (this.validateFilter()) {
        axios
          .post(
            `/user/${this.user.id}/integration/${this.$route().params.integration}/flows/${
              this.$route().params.integrationFlow
            }/filter-options`,
            this.filtersData,
          )
          .then(response => {
            this.filterOptionsData = response.data;
            this.isUpdate = false;
          })
          .catch(({response}) => {
            const message = response.status === 403 ? response.data.message : 'Invalid Query';
            this.handleError(message);
          });
      }
    },

    async fetchOptions() {
      await axios
        .get(
          `/user/${this.user.id}/integration/${this.$route().params.integration}/flows/${
            this.$route().params.integrationFlow
          }/get-filter-options`,
        )
        .then(response => {
          this.filterOptionsData = response.data;
          if (this.filterOptionsData.length > 0) {
            this.filtersData = this.filterOptionsData;
            this.isUpdate = false;
          } else {
            this.isUpdate = true;
          }
        });
    },

    handleUpdate() {
      this.isUpdate = true;
    },

    validateFilter() {
      this.validated = true;
      for (let i = 0; i < this.filtersData.length; i++) {
        if (!this.filtersData[i].field_name.length) {
          this.validated = false;
        }
      }
      return this.validated;
    },
    removeRow(index) {
      this.filtersData.splice(index, 1);
    },

    handleError(error) {
      Swal.fire({
        text: error,
        icon: 'error',
        showConfirmButton: false,
        timer: 2000,
      });
    },
  },
};
</script>

<style scoped>
.delete-button {
  background-color: #d34e59;
  border-radius: 25px;
  color: #fff;
  font-size: 2rem;
  line-height: 50px;
  max-width: 50px;
  text-align: center;
}
</style>
